// Single responsibility: Manage application tenant business themes and dark mode toggling
import { Injectable, effect, inject, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { TenantService } from '../tenant/tenant.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private tenantService = inject(TenantService);
  private darkModeSubject = new BehaviorSubject<boolean>(false);
  public darkMode$: Observable<boolean> = this.darkModeSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      const savedTheme = localStorage.getItem('elixir-dark-mode');
      const isDark = savedTheme ? JSON.parse(savedTheme) : false;
      this.setDarkMode(isDark);
    }

    // Automatically switch theme whenever the tenant's themeClass signal changes
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      
      const activeTheme = this.tenantService.themeClass();
      this.applyTheme(activeTheme);
    });
  }

  /**
   * Toggles the current dark mode state.
   */
  toggleDarkMode(): void {
    this.setDarkMode(!this.darkModeSubject.value);
  }

  /**
   * Sets the dark mode state and updates DOM classes and storage.
   * @param isDark - Whether dark mode is enabled
   */
  setDarkMode(isDark: boolean): void {
    this.darkModeSubject.next(isDark);
    if (!isPlatformBrowser(this.platformId)) return;

    localStorage.setItem('elixir-dark-mode', JSON.stringify(isDark));
    
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('theme-dark', 'elixir-backoffice-app-dark');
    } else {
      root.classList.remove('theme-dark', 'elixir-backoffice-app-dark');
    }
  }

  /**
   * Returns whether dark mode is currently active.
   */
  isDarkMode(): boolean {
    return this.darkModeSubject.value;
  }

  private prefersDarkMode(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  private applyTheme(themeName: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const rootElement = document.documentElement;

    // 1. Remove any previously applied business themes (keep theme-dark / elixir-backoffice-app-dark intact)
    rootElement.classList.forEach((cls) => {
      if (cls.startsWith('theme-') && cls !== 'theme-dark') {
        rootElement.classList.remove(cls);
      }
    });

    // 2. Add the active theme class (e.g., 'theme-retail' or 'theme-mobile')
    if (themeName && themeName !== 'theme-dark') {
      rootElement.classList.add(themeName);
    }
  }
}
